import React from "react";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Activity, ChevronRight } from "lucide-react";
import { useItemDetailsContext } from "./ItemDetailsContext";

const ItemDetailsStatusSection = () => {
    const {
        itemInfo,
        itemDetails,
        itemType,
        handleStatusChange,
    } = useItemDetailsContext();

    // Status color mapping
    const getStatusStyles = (status) => {
        const statusStyles = {
            active: {
                badge: "bg-emerald-500/10 text-emerald-600 border-emerald-500/25 hover:bg-emerald-500/20",
                dot: "bg-emerald-500",
            },
            pending: {
                badge: "bg-amber-500/10 text-amber-600 border-amber-500/25 hover:bg-amber-500/20",
                dot: "bg-amber-500",
            },
            completed: {
                badge: "bg-sky-500/10 text-sky-600 border-sky-500/25 hover:bg-sky-500/20",
                dot: "bg-sky-500",
            },
            archived: {
                badge: "bg-slate-500/10 text-slate-600 border-slate-500/25 hover:bg-slate-500/20",
                dot: "bg-slate-500",
            },
        };
        return statusStyles[status?.toLowerCase()] || {
            badge: "bg-violet-500/10 text-violet-600 border-violet-500/25 hover:bg-violet-500/20",
            dot: "bg-violet-500",
        };
    };

    const availableStatuses = itemInfo.state_machine?.filter(
        (state) => state !== itemDetails.status
    ) || [];

    // Only show for folders with status
    if (itemType !== "folder" || !itemDetails.status) {
        return null;
    }

    return (
        <div className="space-y-4 w-full">
            <div className="flex items-center gap-2.5">
                <div className="flex items-center justify-center size-8 rounded-lg bg-emerald-500/10">
                    <Activity className="size-4 text-emerald-600" />
                </div>
                <div>
                    <h3 className="text-sm font-semibold text-foreground">Status</h3>
                    <p className="text-xs text-muted-foreground">Current workflow state</p>
                </div>
            </div>

            {/* Status options inline */}
            <div className="flex flex-wrap items-center gap-2">
                {/* Current Status Badge */}
                <Badge
                    variant="outline"
                    className={cn(
                        "h-9 px-4 text-sm font-medium gap-2 cursor-default",
                        getStatusStyles(itemDetails.status).badge
                    )}
                >
                    <span className="relative flex size-2">
                        <span
                            className={cn(
                                "animate-ping absolute inline-flex h-full w-full rounded-full opacity-75",
                                getStatusStyles(itemDetails.status).dot
                            )}
                        />
                        <span
                            className={cn(
                                "relative inline-flex rounded-full size-2",
                                getStatusStyles(itemDetails.status).dot
                            )}
                        />
                    </span>
                    {itemDetails.status}
                </Badge>

                {/* Available status options */}
                {availableStatuses.length > 0 && (
                    <>
                        <ChevronRight className="size-4 text-muted-foreground/50" />
                        {availableStatuses.map((state) => (
                            <Badge
                                key={state}
                                variant="outline"
                                onClick={() => handleStatusChange(state)}
                                className={cn(
                                    "h-9 px-4 text-sm font-medium gap-2 cursor-pointer transition-all",
                                    "bg-muted/30 text-muted-foreground border-border/50",
                                    "hover:bg-accent hover:text-accent-foreground hover:border-accent-foreground/20 hover:scale-105"
                                )}
                            >
                                <span
                                    className={cn(
                                        "size-2 rounded-full",
                                        getStatusStyles(state).dot
                                    )}
                                />
                                {state}
                            </Badge>
                        ))}
                    </>
                )}
            </div>
        </div>
    );
};

export default ItemDetailsStatusSection;
